import { Component } from '@theme/component';

/**
 * @typedef {Object} KustomerContactFormRefs
 * @property {HTMLFormElement} contactForm - The contact form element
 * @property {HTMLInputElement} imageUpload - File upload input
 * @property {HTMLButtonElement} submitButton - Submit button
 * @property {HTMLElement} response - Response message element
 */

/**
 * @extends {Component<KustomerContactFormRefs>}
 */
class KustomerContactForm extends Component {
  requiredRefs = ['contactForm', 'imageUpload', 'submitButton', 'response'];
  translations = /** @type {typeof window & { contactFormTranslations?: Record<string, string> }} */ (
    window
  ).contactFormTranslations;
  genericErrorEN = 'Something went wrong. Try again later.'

  /** @type {(() => void) | null} */
  #recaptchaLoadCallback = null;

  connectedCallback() {
    super.connectedCallback();

    const recaptchaSiteKey = this.dataset.recaptchaSiteKey;
    if (recaptchaSiteKey) {
      this.#loadReCaptcha(recaptchaSiteKey);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#recaptchaLoadCallback) {
      window.onloadCallback = undefined;
      this.#recaptchaLoadCallback = null;
    }
  }

  /**
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    this.#setResponse('', false);
    this.#setLoading(true);

    if (!this.#validateForm()) {
      this.#setLoading(false);
      return;
    }

    if (this.dataset.recaptchaSiteKey && !this.#getRecaptchaResponse()) {
      this.#setResponse(this.translations?.recaptchaError || 'Recaptcha Error', true);
      this.#setLoading(false);
      return;
    }

    const formAction = this.refs.contactForm.action;
    if (!formAction) {
      this.#setResponse(this.translations?.error || this.genericErrorEN, true);
      this.#setLoading(false);
      return;
    }

    try {
      const response = await fetch(formAction, {
        method: 'POST',
        body: this.#buildFormData(),
        redirect: 'manual',
      });

      this.#handleResponse(response.ok);
    } catch {
      this.#handleResponse(false);
    }
  }

  /**
   * @returns {boolean}
   */
  #validateForm() {
    const form = this.refs.contactForm;
    const fields = form.querySelectorAll('input, textarea, select');
    let isValid = true;

    for (const field of fields) {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement)) {
        continue;
      }

      if (field.type === 'file') continue;

      field.setAttribute('aria-invalid', 'false');

      if (field.required && !field.value.trim()) {
        field.setAttribute('aria-invalid', 'true');
        isValid = false;
        continue;
      }

      if (field.type === 'email' && field.value.trim() && !this.#isValidEmail(field.value)) {
        field.setAttribute('aria-invalid', 'true');
        isValid = false;
      }
    }

    if (!isValid) {
      this.#setResponse(this.translations?.error || this.genericErrorEN, true);
      const firstInvalid = form.querySelector('[aria-invalid="true"]');
      if (firstInvalid instanceof HTMLElement) {
        firstInvalid.scrollIntoView();
        firstInvalid.focus();
      }
    }

    return isValid;
  }

  /**
   * @param {string} value
   * @returns {boolean}
   */
  #isValidEmail(value) {
    return /\S+@\S+\.\S+/.test(value);
  }

  /**
   * @returns {string}
   */
  #getRecaptchaResponse() {
    if (typeof grecaptcha === 'undefined') {
      return '';
    }

    return grecaptcha.getResponse();
  }

  /**
   * @returns {FormData}
   */
  #buildFormData() {
    const formData = new FormData(this.refs.contactForm);
    const payload = new FormData();

    for (const [key, value] of formData.entries()) {
      if (key === 'contact[image_upload]' || key === 'g-recaptcha-response') continue;
      payload.append(key, value);
    }

    const file = this.refs.imageUpload.files?.[0];
    if (file) {
      payload.append('attachments', file, file.name);
    }

    const recaptchaResponse = this.#getRecaptchaResponse();
    if (recaptchaResponse) {
      payload.append('g-recaptcha-response', recaptchaResponse);
    }

    return payload;
  }

  /**
   * @param {boolean} isOk
   */
  #handleResponse(isOk) {
    if (!isOk) {
      this.#setResponse(this.translations?.error || this.genericErrorEN, true);
      this.#setLoading(false);
      return;
    }

    this.#setResponse(this.translations?.submitSuccess || 'Success', false);
    this.#setLoading(false);
    this.#resetForm();
    this.dispatchEvent(new CustomEvent('kustomer-contact-form:success', { bubbles: true }));
  }

  #resetForm() {
    this.refs.contactForm.reset();

    if (typeof grecaptcha !== 'undefined') {
      grecaptcha.reset();
    }
  }

  /**
   * @param {string} message
   * @param {boolean} isError
   */
  #setResponse(message, isError) {
    const responseEl = this.refs.response;

    if (!message) {
      responseEl.hidden = true;
      responseEl.textContent = '';
      return;
    }

    responseEl.hidden = false;
    responseEl.textContent = message;
    if (isError) {
      responseEl.classList.add('error');
      responseEl.classList.remove('success');
    } else {
      responseEl.classList.add('success');
      responseEl.classList.remove('error');
    }
  }

  /**
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    this.refs.submitButton.disabled = isLoading;
    this.refs.contactForm.setAttribute('aria-busy', String(isLoading));
    this.refs.contactForm.classList.toggle('is-loading', isLoading)
  }

  /**
   * @param {string} siteKey
   */
  #loadReCaptcha(siteKey) {
    const containerId = `${this.refs.contactForm.id}-recaptcha-container`;
    if (document.getElementById(containerId)?.hasChildNodes()) return;

    this.#recaptchaLoadCallback = () => {
      if (typeof grecaptcha === 'undefined') return;
      if (document.getElementById(containerId)?.hasChildNodes()) return;

      grecaptcha.render(containerId, { sitekey: siteKey });
    };

    window.onloadCallback = this.#recaptchaLoadCallback;

    const existingScript = document.querySelector('script[src*="google.com/recaptcha/api.js"]');
    if (existingScript) {
      if (typeof grecaptcha !== 'undefined') {
        grecaptcha.ready(this.#recaptchaLoadCallback);
      }
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?onload=onloadCallback&render=explicit';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }
}

customElements.define('kustomer-contact-form', KustomerContactForm);
